/**
 * C0-GEN — editorial-layer resolver (Wave 1, Phase 0/1).
 *
 * The SINGLE entry point every monograph render block uses to read editorial
 * content. It NEVER reads `report.editorialNarrative` directly — it resolves
 * through the deterministic fallback so the render can never depend on the
 * generated layer existing:
 *
 *   resolveEditorialLayer(report, sectionId)
 *     = report.editorialNarrative?.sections[sectionId]   // generated (Wave 2+)
 *     ?? composeEditorialFallback(report, sectionId)      // deterministic Option-A
 *
 * In Wave 1 no generator writes `editorialNarrative`, so this ALWAYS returns
 * the deterministic fallback — which returns the SAME verified strings the
 * legacy renderers display. That is the byte-identical guarantee.
 *
 * Pure + total: no I/O, no `Date.now`/`Math.random`, never throws.
 */
import type { SiteRagReport, EditorialSectionLayer } from '../../types/siteRag';
import { composeEditorialFallback } from './composeEditorialFallback';

export function resolveEditorialLayer(
  report: SiteRagReport,
  sectionId: string,
): EditorialSectionLayer {
  const generated = report.editorialNarrative?.sections?.[sectionId];
  if (generated) return generated;
  return composeEditorialFallback(report, sectionId);
}

/**
 * `true` iff a GENERATED editorial layer exists for `sectionId` (i.e. the
 * generator ran with the flag ON and the grounding validators accepted that
 * section), as opposed to the deterministic Option-A fallback.
 *
 * This is the SINGLE gate the Wave-3 monograph RESTRUCTURE keys on (the
 * full-prose body, the suppression of the superseded labelled "Assessment" /
 * "Recommended Action" blocks, the pull-quote, the dramatic opener thesis).
 * The visual chrome flag (`isEditorialLayoutsEnabled`) controls the masthead /
 * hero-numeral / 2-col shell; this controls whether we trust a section's prose
 * enough to demote its structured sub-blocks. When it returns `false` (flag was
 * OFF at gen-time, legacy report, or that section's layer was rejected) the
 * render keeps today's structured blocks + empty-states BYTE-IDENTICALLY — the
 * `reportSurfaceParity` fixtures carry no `editorialNarrative`, so they always
 * take this path.
 *
 * Pure + total: never throws.
 */
export function hasGeneratedEditorialLayer(
  report: SiteRagReport,
  sectionId: string,
): boolean {
  const layer = report.editorialNarrative?.sections?.[sectionId];
  return (
    layer != null
    && typeof layer.editorialNarrative === 'string'
    && layer.editorialNarrative.trim().length > 0
  );
}

/**
 * Convenience for the render call-sites: resolve a section's HEADLINE,
 * falling back to the caller's existing literal default when neither a
 * generated layer nor the registry provides one.
 *
 * Equivalent to `resolveEditorialLayer(report, sectionId).editorialHeadline
 * ?? defaultLabel`. In Wave 1, `editorialHeadline` is the registry label
 * (when known) or `undefined` (when not) — both of which keep the rendered
 * heading byte-identical to today's `defaultSectionHeading(...)` output.
 */
export function resolveEditorialHeadline(
  report: SiteRagReport,
  sectionId: string,
  defaultLabel: string,
): string {
  return resolveEditorialLayer(report, sectionId).editorialHeadline ?? defaultLabel;
}
